import React, { memo, forwardRef, useState, useCallback, useRef } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';
import {
  Text,
  IconArrowDown,
  Space,
  IconArrowUp,
} from '@redlink/components/atoms';
import { createUseStyle } from '@redlink/assets/styles';
import { useTimingTransition, mix } from 'react-native-redash';
import Animated, { multiply, sub } from 'react-native-reanimated';

export const DropDown = memo(
  forwardRef(
    (
      {
        style,
        text,
        placeholder,
        data = [],
        onChange,
        renderItem,
        renderSelectedItem,
        containerLayout,
        horizontal = false,
        require = false,
        title,
      },
      ref
    ) => {
      const [isVisible, setIsVisible] = useState(false);
      const [layout, setLayout] = useState({});
      const elementRef = useRef(null);
      const { styles } = useStyle();

      const onLayout = useCallback(() => {
        elementRef.current.measure((fx, fy, width, height, px, py) => {
          setLayout({ x: px, y: py, width, height });
        });
      }, []);

      const onSelectItem = useCallback(
        (item, index) => {
          onChange && onChange(item, index);
          setIsVisible(false);
        },
        [onChange]
      );

      const progress = useTimingTransition(isVisible);
      const y = mix(
        progress,
        0,
        multiply(-1, sub(layout.y, containerLayout.y))
      );
      const height = mix(progress, layout.height, containerLayout.height);

      return (
        <Animated.View
          style={[
            styles.mainContainer,
            { height, transform: [{ translateY: y }] },
          ]}
        >
          <DropDownButton
            {...{
              isVisible,
              style,
              title,
              require,
              renderSelectedItem,
              text,
              placeholder,
              onLayout,
            }}
            ref={elementRef}
            onPress={() => setIsVisible((prev) => !prev)}
          />
          <Space />
          {isVisible && (
            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={horizontal && styles.horizontal}
            >
              {data.map((item, index) => (
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={() => onSelectItem(item, index)}
                >
                  {renderItem(item, index)}
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
        </Animated.View>
      );
    }
  )
);

export const DropDownButton = memo(
  forwardRef(
    (
      {
        require = false,
        style,
        isVisible,
        onPress,
        renderSelectedItem,
        onLayout,
        text,
        title,
        placeholder,
      },
      ref
    ) => {
      const { styles, colors } = useStyle();
      const value = text || placeholder;

      return (
        <View {...{ onLayout, ref }}>
          {title && (
            <>
              <Text style={styles.titleText}>{title}</Text>
              <Space size={4} />
            </>
          )}
          <TouchableOpacity
            {...{ onPress }}
            activeOpacity={0.7}
            style={[style, styles.container]}
          >
            <View style={styles.valueContainer}>
              {renderSelectedItem?.()}
              {!renderSelectedItem && (
                <Text
                  style={[styles.titleText, !text && { color: colors.gray21 }]}
                >
                  {value}
                  {!text && require && (
                    <Text style={styles.requireText}>*</Text>
                  )}
                </Text>
              )}
            </View>
            {isVisible ? <IconArrowUp /> : <IconArrowDown />}
          </TouchableOpacity>
        </View>
      );
    }
  )
);

const useStyle = createUseStyle(({ colors }) => ({
  mainContainer: {
    backgroundColor: colors.white,
    overflow: 'hidden',
  },
  container: {
    backgroundColor: colors.gray7,
    borderRadius: 5,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleText: {
    flex: 1,
    fontSize: 14,
    color: colors.black,
  },
  valueContainer: {
    flex: 1,
  },
  horizontal: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  requireText: {
    color: colors.red1,
    fontSize: 10,
    marginStart: 5,
  },
}));
